import React, { useState, useEffect } from 'react';
import { Slider } from 'antd';
import './VolumeAdjustmentPopup.scss';

interface VolumeAdjustmentPopupProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (volume: number, applyToAll: boolean) => void;
  initialVolume?: number;
  position?: {
    top: number;
    left: number;
  };
  loading?: boolean;
  /** 是否显示“应用到全部音频”勾选框，默认显示 */
  showApplyToAll?: boolean;
}

const VolumeAdjustmentPopup: React.FC<VolumeAdjustmentPopupProps> = ({
  visible,
  onClose,
  onConfirm,
  initialVolume = 0,
  position = { top: 0, left: 0 },
  loading = false,
  showApplyToAll = true
}) => {
  const [volume, setVolume] = useState(initialVolume);
  const [applyToAll, setApplyToAll] = useState(false);

  useEffect(() => {
    setVolume(initialVolume);
  }, [initialVolume]);

  const handleVolumeChange = (newVolume: number) => {
    // 限制音量范围在0.5到2.0之间
    const clampedVolume = Math.max(0.5, Math.min(2.0, newVolume));
    setVolume(clampedVolume);
  };

  const handleSliderChange = (value: number) => {
    handleVolumeChange(value);
  };

  const handleSliderAfterChange = (value: number) => {
    // 预留：需要时可在此触发较重的计算或预览更新
    handleVolumeChange(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 移除末尾的'x'字符
    const numericValue = value.replace('x', '');
    const newVolume = parseFloat(numericValue);
    
    if (!isNaN(newVolume)) {
      handleVolumeChange(newVolume);
    }
  };

  const handleIncrement = () => {
    handleVolumeChange(volume + 0.1);
  };

  const handleDecrement = () => {
    handleVolumeChange(volume - 0.1);
  };

  const handleConfirm = () => {
    onConfirm(volume, applyToAll);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="volume-adjustment-overlay" onClick={onClose}>
      <div 
        className="volume-adjustment-popup"
        style={{
          top: position.top,
          left: position.left
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* 标题 */}
        <div className="popup-title">
          音量调节
          {loading && <span className="loading-indicator">加载中...</span>}
        </div>

        {/* 音量控制区域 */}
        <div className="volume-control-section">
          {/* 滑块（与时间轴缩放一致使用 antd Slider） */}
          <div className="volume-slider-container">
            <Slider
              min={0.5}
              max={2.0}
              step={0.1}
              value={volume}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
              tooltip={{ open: false }}
              style={{ width: '100%' }}
            />
            <div className="slider-ticks">
              <div className="tick" style={{ left: '0%' }}></div>
              <div className="tick" style={{ left: '25%' }}></div>
              <div className="tick" style={{ left: '50%' }}></div>
              <div className="tick" style={{ left: '75%' }}></div>
              <div className="tick" style={{ left: '100%' }}></div>
            </div>
          </div>

          {/* 数值输入框和调节按钮 */}
          <div className="volume-input-container">
            <input
              type="text"
              value={`${volume.toFixed(1)}x`}
              onChange={handleInputChange}
              className="volume-input"
            />
            <div className="volume-spinner">
              <button
                type="button"
                className="spinner-btn spinner-up"
                onClick={handleIncrement}
                disabled={volume >= 2.0}
              >
                ▲
              </button>
              <button
                type="button"
                className="spinner-btn spinner-down"
                onClick={handleDecrement}
                disabled={volume <= 0.5}
              >
                ▼
              </button>
            </div>
          </div>
        </div>

        {/* 底部选项和按钮 */}
        <div
          className="popup-footer"
          style={{ justifyContent: showApplyToAll ? 'space-between' : 'flex-end' }}
        >
          {showApplyToAll && (
            <label className="apply-to-all-checkbox">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
              <span className="checkbox-text">应用到全部音频。</span>
            </label>
          )}
          
          <button
            type="button"
            className="confirm-btn"
            onClick={handleConfirm}
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
};

export default VolumeAdjustmentPopup;
